// src/pages/UsersPage.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import Modal from "../components/Modal"; // Assuming you have this from MovesPage

function normalizeUsersResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.rows)) return data.rows;
  return [];
}

function apiErrText(e) {
  const status = e?.response?.status;
  const body = e?.response?.data;
  const msg =
    body?.error ||
    body?.message ||
    (typeof body === "string" ? body : null) ||
    e?.message ||
    "Request failed";
  return status ? `${status}: ${msg}` : msg;
}

function toNumId(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [drvById, setDrvById] = useState({});
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Form fields for create/edit
  const [form, setForm] = useState({
    email: "",
    password: "",
    display_name: "",
    is_active: true,
    is_superadmin: false,
    driver_id: "",
  });

  const sorted = useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
    return copy;
  }, [rows]);

  const load = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/admin/users");
      setRows(normalizeUsersResponse(data));
    } catch (e) {
      setMsg(apiErrText(e));
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  const loadDrivers = async () => {
    try {
      const { data } = await api.get("/driver");
      const list = Array.isArray(data) ? data : [];
      setDrivers(list);
      const byId = {};
      for (const d of list) {
        byId[d.DriverId] = d;
      }
      setDrvById(byId);
    } catch (e) {
      console.error("Failed to load drivers", e);
    }
  };

  useEffect(() => {
    load();
    loadDrivers();
  }, []);

  const createOrUpdate = async () => {
    const payload = {
      email: String(form.email || "").trim(),
      display_name: String(form.display_name || "").trim(),
      is_active: !!form.is_active,
      is_superadmin: !!form.is_superadmin,
      driver_id: toNumId(form.driver_id) || null,
    };

    if (editMode && form.password) {
      payload.password = String(form.password || "");
    } else if (!editMode) {
      payload.password = String(form.password || "");
      if (!payload.email || !payload.password) {
        setMsg("Email + password required");
        return;
      }
    }

    setBusy(true);
    setMsg("");
    try {
      if (editMode) {
        await api.patch(`/admin/users/${selectedUser.id}`, payload);
        setMsg("User updated.");
      } else {
        await api.post("/admin/users", payload);
        setMsg("User created.");
      }
      resetForm();
      await load();
      closeModal();
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async (id) => {
    const pw = window.prompt("New password (leave blank to cancel):");
    if (!pw) return;

    setBusy(true);
    setMsg("");
    try {
      await api.post(`/admin/users/${id}/reset-password`, { password: pw });
      setMsg("Password updated.");
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;

    setBusy(true);
    setMsg("");
    try {
      await api.delete(`/admin/users/${id}`);
      await load();
    } catch (e) {
      setMsg(apiErrText(e));
    } finally {
      setBusy(false);
    }
  };

  const openModal = (user = null) => {
    setEditMode(!!user);
    if (user) {
      setForm({
        email: user.email || "",
        password: "",
        display_name: user.display_name || "",
        is_active: String(user.is_active) === "1",
        is_superadmin: String(user.is_superadmin) === "1",
        driver_id: user.driver_id || "",
      });
      setSelectedUser(user);
    } else {
      resetForm();
      setSelectedUser(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const resetForm = () => {
    setForm({
      email: "",
      password: "",
      display_name: "",
      is_active: true,
      is_superadmin: false,
      driver_id: "",
    });
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>Users</h2>
        <button className="btn-outline" onClick={load} disabled={busy}>
          Refresh
        </button>
        <button className="btn" onClick={() => openModal()} disabled={busy}>
          Add User
        </button>
      </div>

      {msg ? (
        <div className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {msg}
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Existing users</div>
        {busy ? <div className="muted">Loading…</div> : null}

        <div style={{ display: "grid", gap: 10 }}>
          {sorted.map((u) => {
            const active = String(u.is_active) === "1";
            const superadmin = String(u.is_superadmin) === "1";
            const drvId = toNumId(u.driver_id);
            const drvRec = drvId != null ? drvById[drvId] : null;

            return (
              <div key={u.id} className="card" style={{ background: "rgba(255,255,255,0.02)" }}>
                <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>
                      {u.email} {superadmin ? <span className="muted">• superadmin</span> : null}
                    </div>
                    <div className="muted" style={{ marginTop: 4 }}>
                      id={u.id} • {u.display_name || "(no name)"} • active={String(u.is_active)} • superadmin={String(u.is_superadmin)} • driver={drvRec ? drvRec.FullName : "(none)"}
                    </div>
                  </div>

                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <button className="btn-outline" disabled={busy} onClick={() => openModal(u)}>
                      Edit
                    </button>
                    <button className="btn-outline" disabled={busy} onClick={() => resetPassword(u.id)}>
                      Reset Password
                    </button>
                    <button className="btn-outline" disabled={busy} onClick={() => del(u.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {!busy && sorted.length === 0 ? <div className="muted">No users found.</div> : null}
        </div>
      </div>

      {showModal && (
        <Modal onClose={closeModal}>
          <h2>{editMode ? "Edit User" : "Create User"}</h2>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <input
              className="input"
              placeholder="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            {!editMode || form.password ? (
              <input
                className="input"
                placeholder="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            ) : null}
            <input
              className="input"
              placeholder="display name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            />

            <label className="row" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Active
            </label>

            <label className="row" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={form.is_superadmin}
                onChange={(e) => setForm({ ...form, is_superadmin: e.target.checked })}
              />
              Superadmin
            </label>

            <select className="input" value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })}>
              <option value="">No Driver</option>
              {drivers.map((drv) => (
                <option key={drv.DriverId} value={drv.DriverId}>
                  {drv.FullName || `Driver #${drv.DriverId}`}
                </option>
              ))}
            </select>

            <button className="btn" onClick={createOrUpdate} disabled={busy}>
              {editMode ? "Update" : "Create"}
            </button>
          </div>

          <button className="btn-outline" onClick={closeModal} disabled={busy}>
            Close
          </button>
        </Modal>
      )}
    </div>
  );
}